import { LANGUAGES } from '../constants/language-constants';
import { getI18nLanguageFromLocalStorage } from './localStorageHelper';
const BaseURL = process.env.API_ENDPOINT;

/**
 * Retrieves the localized display name for a given language code.
 *
 * @param {string} languageCode - The BCP 47 language code (e.g., 'en', 'es-MX').
 * @param {string} [displayLocale] - Optional. The locale in which to display the language name. Defaults to the user's current i18next language or the application's default language.
 * @returns {string} The localized display name of the language, or the original code if a display name cannot be determined.
 */
export const getLanguageDisplayName = (languageCode, displayLocale = null) => {
    if (!languageCode || typeof languageCode !== 'string') {
        console.warn("getLanguageDisplayName: Invalid language code provided:", languageCode);
        return String(languageCode); // Return the input as a string (handles null, undefined, etc.)
    }

    const i18nextLng = displayLocale || getI18nLanguageFromLocalStorage() || LANGUAGES.ENGLISH;

    try {
        // Ensure consistent format: "Language (Country)"
        const parts = languageCode.split("-");
        if (parts.length === 2) {
            const languageCode = parts[0];
            const countryCode = parts[1];
            
            // Get language name without region
            const languageOnly = new Intl.DisplayNames([i18nextLng], { type: "language" });
            const languageName = languageOnly.of(languageCode.toLowerCase());
            
            // Get region name
            const regions = new Intl.DisplayNames([i18nextLng], { type: "region" });
            const regionName = regions.of(countryCode.toUpperCase());
            
            return `${languageName} (${regionName})`;
        }
        
        // Fallback for simple language codes (without country)
        const languages = new Intl.DisplayNames([i18nextLng], { type: "language" });
        return languages.of(languageCode.toLowerCase()); // Use toLowerCase() for broader compatibility
    } catch (error) {
        //console.error(`getLanguageDisplayName: Error displaying language name for ${languageCode} in locale ${i18nextLng}:`, error);
        return languageCode; // Fallback to the original code
    }
};

/**
 * Fetches an array of language codes from a JSON file.
 *
 * @returns {Promise<string[]>} A promise that resolves to an array of language codes (strings), or an empty array on error.
 */
export const fetchLanguageCodes = async () => {
    try {
        const response = await fetch(`${BaseURL}/data/locales.json`);
        if (!response.ok) {
            throw new Error(`Failed to fetch language codes. Status: ${response.status} ${response.statusText}`);
        }
        const locales = await response.json();

        // Validate that the response is an array of strings.
        if (!Array.isArray(locales) || !locales.every(item => typeof item === 'string')) {
            throw new Error("fetchLanguageCodes: Invalid data format. Expected an array of strings.");
        }

        return locales;
    } catch (error) {
        //console.error("Error fetching language codes:", error);
        return [];
    }
};

/**
 * Generates an array of language options (code and display name) for UI elements.
 *
 * @returns {Promise<{code: string, name: string}[]>} A promise that resolves to an array of objects, each with 'code' (the language code) and 'name' (the localized display name) properties.  Returns an empty array on error.
 */
export const getLanguageOptions = async (displayLocale = null) => {
    try {
        const localeCodes = await fetchLanguageCodes();
        
        if (!localeCodes || localeCodes.length === 0) {
            console.warn("No language codes found to map.");
            return [];
        }

        const languageList = localeCodes
            .map((code) => {
                try {
                    const displayName = getLanguageDisplayName(code, displayLocale);
                    const enUSName = getLanguageDisplayName(code, "en-US");
                    const viVNName = getLanguageDisplayName(code, "vi-VN");
                    const koKRName = getLanguageDisplayName(code, "ko-KR");
                    let languageFlag = "";

                    const parts = code.split("-");
                    if (parts.length === 2) {
                        languageFlag = `fi fi-${parts[1].toLowerCase()}`;
                    }

                    return {
                        code: code,
                        name: displayName,
                        alternativeNames: [displayName, enUSName, viVNName, koKRName],
                        flag: languageFlag,
                    };
                } catch (innerError) {
                    //console.error(`Error processing language code ${code}:`, innerError);
                    return null;
                }
            })
            .filter((locale) => locale !== null)
            .sort((a, b) => a.name.localeCompare(b.name, displayLocale || undefined));

        return languageList;
    } catch (error) {
        //console.error("Error creating language options list:", error);
        return [];
    }
};

export const getCurrentLanguage = () => {
    return getI18nLanguageFromLocalStorage() || LANGUAGES.ENGLISH;
}